import { BreedInterestController } from '@application/components';
import { getRandomIntInclusive, useRouter } from '@application/utils';
import { css } from '@emotion/react';
import {
  Banner,
  CardItemBreeder,
  CardItemLitter,
  Container,
  DetailList,
  IconScalePaws,
  Section,
  SectionCardItemSlider,
  SectionSubtitle,
  SectionText,
  SideLayoutSideWrapper,
  SideLayoutWrapper,
  StoreBanner,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMediaQuery } from 'react-responsive';
import { BREAKPOINTS } from '@presentation/styles';
import { useMemo } from 'react';
import { TbDiscount2, TbDna } from 'react-icons/tb';
import bannerBackgroundImage from '@assets/banner-background-image.svg';
import type { BreedPageProps } from '..';
import bannerImage from './assets/banner-image.webp';
import * as Styled from './styled';

export * from './types';

export const BreedPage: FC<BreedPageProps> = (props) => {
  const {
    name,
    title,
    description,
    content,
    details,
    attributeCategories,
    genetics,
    images,
    moreBreedersLink,
    moreLittersLink,
    breederItems = [],
    breederItemsLoading,
    litterItems = [],
    litterItemsLoading,
    subscribeBtn,
    showLittersCta = true,
    showBreedersCta = true,
    ...rest
  } = props;
  const { translate } = useTranslation();
  const { navigateToRoute } = useRouter();

  const isMobile = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.phone}px)` });

  const litterCards = useMemo(() => {
    if (litterItemsLoading) {
      return Array(getRandomIntInclusive(1, 3))
        .fill(undefined)
        .map((_, index) => (
          <CardItemLitter
            style={{ height: 327.5 }}
            key={index}
            loading
            breed=""
            price=""
            location=""
          />
        ));
    }
    return litterItems.map((item, index) => (
      <CardItemLitter style={{ height: 327.5 }} key={index} {...item} />
    ));
  }, [litterItems, litterItemsLoading]);

  const breederCards = useMemo(() => {
    if (breederItemsLoading) {
      return Array(getRandomIntInclusive(1, 3))
        .fill(undefined)
        .map((_, index) => <CardItemBreeder style={{ height: 375 }} key={index} loading />);
    }
    return breederItems.map((item, index) => (
      <CardItemBreeder style={{ height: 375 }} key={index} {...item} />
    ));
  }, [breederItems, breederItemsLoading]);

  return (
    <>
      {images?.background && <Styled.CoverImage src={images.background} />}
      <Container
        css={css`
          ${!images?.background && 'margin-top: 100px;'}
        `}
      >
        <SideLayoutWrapper {...rest}>
          <SideLayoutSideWrapper>
            <Styled.SideContentWrapper>
              {images?.profile && (
                <Styled.ImageWrapperContainer>
                  <Styled.MainImage src={images.profile} alt={title} />
                </Styled.ImageWrapperContainer>
              )}
              <Styled.TitleSubscribeWrapperMobile>
                <Styled.Title>{title}</Styled.Title>
                {subscribeBtn}
              </Styled.TitleSubscribeWrapperMobile>
              {showBreedersCta || showLittersCta ? (
                <Styled.SideButtonContainer>
                  {showBreedersCta && (
                    <Styled.SideButton color="blue" link={moreBreedersLink}>
                      {translate('common.buttons.seeBreedings')}
                    </Styled.SideButton>
                  )}
                  {showLittersCta && (
                    <Styled.SideButton color="blue" link={moreLittersLink}>
                      {translate('common.buttons.seeLitters')}
                    </Styled.SideButton>
                  )}
                </Styled.SideButtonContainer>
              ) : null}
              <Styled.InfoTitle>{translate('breedDetails.titleCharacteristic')}</Styled.InfoTitle>
              <DetailList items={details ?? []} />
              {(attributeCategories ?? []).map((attributeCategory, index) => (
                <div key={index}>
                  <Styled.InfoTitle>{attributeCategory.categoryName}</Styled.InfoTitle>
                  {attributeCategory.attributes.map((attribute, aIndex) => (
                    <Styled.InfoAttributeWrapper key={aIndex}>
                      <Styled.InfoAttributeLabel>{attribute.label}</Styled.InfoAttributeLabel>
                      <IconScalePaws amount={attribute.value} />
                    </Styled.InfoAttributeWrapper>
                  ))}
                </div>
              ))}
              <Styled.InfoTitle>{translate('breedDetails.genetics.title')}</Styled.InfoTitle>

              {genetics && (
                <Styled.SideGeneticsSubtitle dangerouslySetInnerHTML={{ __html: genetics.text }} />
              )}

              {genetics && (
                <Styled.SideGeneticsList>
                  {genetics.items.map((item, index) => (
                    <Styled.SideGeneticsItem key={index}>
                      <Styled.SideGeneticsIconWrapper>
                        <TbDna />
                      </Styled.SideGeneticsIconWrapper>

                      <Styled.SideGeneticsTextWrapper>{item}</Styled.SideGeneticsTextWrapper>
                    </Styled.SideGeneticsItem>
                  ))}
                </Styled.SideGeneticsList>
              )}
              <Styled.SideGeneticsWrapper>
                <Styled.SideGeneticsInfoBox>
                  <Styled.SideGeneticsInfoBoxTitle>
                    <TbDiscount2 />
                    <span>{translate('breedDetails.genetics.infos.title')}</span>
                  </Styled.SideGeneticsInfoBoxTitle>
                  <Styled.SideGeneticsInfoBoxText>
                    {translate('breedDetails.genetics.infos.text')}
                  </Styled.SideGeneticsInfoBoxText>
                </Styled.SideGeneticsInfoBox>
              </Styled.SideGeneticsWrapper>
            </Styled.SideContentWrapper>

            <Styled.SideBannerWrapper>
              <StoreBanner useMobileImage theme="lightBlue" source="breed" />
            </Styled.SideBannerWrapper>
          </SideLayoutSideWrapper>
          <Styled.StyledSideLayoutContentWrapper hasBg={!!images?.background}>
            <Styled.TitleSubscribeWrapperDesktop>
              <Styled.Title>{title}</Styled.Title>
              {subscribeBtn}
            </Styled.TitleSubscribeWrapperDesktop>
            {description?.length ? (
              <SectionText>{description}</SectionText>
            ) : (
              <Styled.DefaultContent
                dangerouslySetInnerHTML={{
                  __html: translate('breedDetails.defaultDescription', { defaultValue: '' }),
                }}
              />
            )}
            {!isMobile ? (
              <Styled.BannerWrapper>
                <Banner
                  title={translate('common.banners.quiz.title')}
                  btnText={translate('common.buttons.takeTest')}
                  theme="blue"
                  backgroundImage={bannerBackgroundImage}
                  image={bannerImage}
                  onClick={() => navigateToRoute('quiz')}
                />
              </Styled.BannerWrapper>
            ) : null}

            {!!content && (
              <Section>
                {Object.keys(content)
                  .filter((contentKey) => !!content[contentKey as keyof typeof content])
                  .map((contentKey, index) => (
                    <div key={index}>
                      {isMobile && contentKey === 'historic' ? (
                        <Banner
                          title={translate('common.banners.quiz.title')}
                          btnText={translate('common.buttons.takeTest')}
                          theme="blue"
                          backgroundImage={bannerBackgroundImage}
                          image={bannerImage}
                          onClick={() => navigateToRoute('quiz')}
                          noArrow
                          center
                        />
                      ) : null}
                      <SectionSubtitle>{translate(`breedDetails.${contentKey}`)}</SectionSubtitle>
                      <SectionText>{content[contentKey as keyof typeof content]}</SectionText>
                    </div>
                  ))}
              </Section>
            )}
          </Styled.StyledSideLayoutContentWrapper>
        </SideLayoutWrapper>
      </Container>

      {litterCards.length > 0 && (
        <Styled.SectionWrapperLitters>
          <Container>
            <SectionCardItemSlider
              title={
                <Styled.SectionTitle color="blue">
                  {translate('common.sections.breedLitters').replace('$breed', title ?? '')}
                </Styled.SectionTitle>
              }
              loading={litterItemsLoading}
              items={litterCards}
              showMoreLink={moreLittersLink}
              whiteArrows
              outlinedButtons={false}
            />
          </Container>
        </Styled.SectionWrapperLitters>
      )}

      {breederCards.length > 0 && (
        <Container>
          <SectionCardItemSlider
            title={translate('common.sections.breedBreedings').replace('$breed', title ?? '')}
            loading={breederItemsLoading}
            items={breederCards}
            showMoreLink={moreBreedersLink}
          />
        </Container>
      )}

      <Styled.VerticalMargin />

      <Container>
        <BreedInterestController preselectedBreedName={name ? [name] : []} />
      </Container>

      <Styled.VerticalMargin />
    </>
  );
};
